import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const NAV_ITEMS = [
    { id: 'home',      label: 'Home' },
    { id: 'home',      label: 'Services', key: 'services-link' },
    { id: 'pricing',   label: 'Pricing' },
    { id: 'website-demo', label: 'AI Demo' },
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'security',  label: 'Security' },
    { id: 'about',     label: 'About' },
    { id: 'for-good',  label: 'For Good', path: '/for-good' },
    { id: 'process',   label: 'Process' },
    { id: 'faq',       label: 'FAQ' },
];

const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [overHero, setOverHero] = useState(false);
    const [activeId, setActiveId] = useState('home');
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 8);
            // Dark navbar while the hero section is occupying the viewport top
            const onOnePage = location.pathname === '/';
            setOverHero(onOnePage && window.scrollY < window.innerHeight * 0.7);
        };
        onScroll();
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, [location.pathname]);

    // Close menu on route change
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Track which section is in view to highlight the active nav link (only on /)
    useEffect(() => {
        if (location.pathname !== '/') return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id);
                    }
                });
            },
            { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
        );
        NAV_ITEMS.forEach((item) => {
            if (item.path) return;
            const el = document.getElementById(item.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [location.pathname]);

    const handleNav = (item) => (e) => {
        e.preventDefault();
        if (item.path) {
            navigate(item.path);
            setOpen(false);
            return;
        }
        const id = item.id;
        if (location.pathname !== '/') {
            // Navigate to one-page root with hash; OnePage useEffect will scroll
            navigate(`/#${id}`);
        } else {
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // Update URL hash without re-triggering navigation
                window.history.replaceState(null, '', `/#${id}`);
            }
        }
        setOpen(false);
    };

    const isActive = (item) => {
        if (item.path) return location.pathname === item.path;
        return location.pathname === '/' && activeId === item.id;
    };

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                overHero
                    ? 'bg-[#020815]/55 backdrop-blur-xl border-b border-white/[0.08]'
                    : scrolled
                    ? 'bg-white/85 backdrop-blur-xl border-b border-[#E8E8EC] shadow-[0_1px_0_rgba(26,26,46,0.02)]'
                    : 'bg-white/60 backdrop-blur-md border-b border-transparent'
            }`}
            data-testid="site-navbar"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 h-[72px] flex items-center gap-4 lg:gap-6">
                {/* Logo */}
                <Link
                    to="/"
                    onClick={handleNav({ id: 'home' })}
                    className="flex items-center shrink-0"
                    data-testid="nav-logo"
                    aria-label="RevoloAIWEB — home"
                >
                    <img
                        src="/logo.png"
                        alt="RevoloAIWEB"
                        className="h-12 sm:h-14 w-auto select-none"
                        draggable="false"
                    />
                </Link>

                {/* Desktop links — visible at lg+ (1024px) */}
                <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
                    {NAV_ITEMS.map((item) => (
                        <a
                            key={item.key || item.id}
                            href={item.path || `/#${item.id}`}
                            onClick={handleNav(item)}
                            className={`px-3.5 py-2 rounded-lg text-[14px] font-medium transition-all duration-200 ${
                                overHero
                                    ? isActive(item)
                                        ? 'text-white bg-white/[0.12]'
                                        : 'text-slate-200 hover:text-white hover:bg-white/[0.08]'
                                    : isActive(item)
                                    ? 'text-[#1A1A2E] bg-[#F2F0FE]'
                                    : 'text-[#4A4A5A] hover:text-[#1A1A2E] hover:bg-[#F7F7F8]'
                            }`}
                            data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                {/* CTA — desktop only (lg+) */}
                <Link
                    to="/contact"
                    className={`hidden lg:inline-flex items-center gap-1.5 font-semibold text-[13px] px-5 py-2.5 rounded-full transition-all duration-300 shrink-0 ${
                        overHero
                            ? 'bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] text-white shadow-[0_8px_24px_rgba(59,130,246,0.45)] hover:opacity-95'
                            : 'bg-[#1A1A2E] text-white hover:bg-[#7B61FF]'
                    }`}
                    data-testid="nav-cta-quote"
                >
                    Get a Quote
                </Link>

                {/* Hamburger — visible on mobile + tablet (< lg) */}
                <button
                    type="button"
                    className={`lg:hidden ml-auto p-2 rounded-lg transition-colors ${
                        overHero
                            ? 'text-white hover:bg-white/[0.08]'
                            : 'text-[#1A1A2E] hover:bg-[#F7F7F8]'
                    }`}
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {/* Mobile + tablet menu */}
            {open && (
                <div
                    className="lg:hidden bg-white border-t border-[#E8E8EC] shadow-soft"
                    data-testid="mobile-menu"
                >
                    <div className="px-6 py-5 flex flex-col gap-1">
                        {NAV_ITEMS.map((item) => (
                            <a
                                key={item.key || item.id}
                                href={item.path || `/#${item.id}`}
                                onClick={handleNav(item)}
                                className={`px-3 py-2.5 rounded-lg text-[15px] font-medium ${
                                    isActive(item)
                                        ? 'text-[#1A1A2E] bg-[#F2F0FE]'
                                        : 'text-[#4A4A5A]'
                                }`}
                                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                {item.label}
                            </a>
                        ))}
                        <Link
                            to="/contact"
                            onClick={() => setOpen(false)}
                            className="mt-3 inline-flex items-center justify-center bg-[#1A1A2E] text-white font-semibold text-[14px] px-5 py-3 rounded-full"
                            data-testid="mobile-nav-cta"
                        >
                            Get a Quote
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
